/** Candidate and User Control Deck. Every field here is a row or a count. */

export type Segment = "all" | "pass" | "expiring" | "offers" | "dormant";

export type Counts = {
  readonly applications: number;
  readonly liveApplications: number;
  readonly offers: number;
  readonly rounds: number;
  readonly debriefs: number;
  readonly mocks: number;
  readonly competencies: number;
};

export type Pass = {
  readonly subscriptionId: string | null;
  readonly planId: string | null;
  readonly status: "active" | "cancelled" | "expired" | "granted";
  readonly planName: string | null;
  readonly startedAt: number;
  readonly expiresAt: number;
  readonly grantedBy: string | null;
  readonly cancelledAt?: number | null;
};

export type RosterRow = {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly createdAt: number;
  readonly lastSeenAt: number;
  readonly targetTrack: string | null;
  readonly yearsExperience: number | null;
  readonly ctcTargetMinInr: number | null;
  readonly ctcTargetMaxInr: number | null;
  readonly pass: Pass | null;
  readonly companies: readonly string[];
  readonly counts: Counts;
};

export type Totals = {
  readonly candidates: number;
  readonly awaitingDeletion: number;
  readonly inSearch: number;
  readonly liveApplications: number;
  readonly offersAccepted: number;
  readonly offerCandidates: number;
  readonly debriefs: number;
  readonly debriefsRead: number;
  readonly activePasses: number;
  readonly expiringPasses: number;
  readonly grantedPasses: number;
  readonly plans: number;
};

export type TrackTally = { readonly track: string; readonly count: number };

export type Roster = {
  readonly ok: true;
  readonly segment: Segment;
  readonly query: string;
  readonly track: string;
  readonly page: number;
  readonly pages: number;
  readonly pageSize: number;
  readonly pageSizes: readonly number[];
  readonly exportCap: number;
  readonly total: number;
  readonly segments: readonly { readonly key: Segment; readonly count: number }[];
  readonly tracks: readonly TrackTally[];
  readonly totals: Totals;
  readonly rows: readonly RosterRow[];
};

export type RosterExport = {
  readonly ok: true;
  readonly exportedAt: number;
  readonly audited: true;
  readonly segment: Segment;
  readonly query: string;
  readonly track: string;
  readonly matching: number;
  readonly cap: number;
  readonly rows: readonly RosterRow[];
};

/** The reserved filter value for an account with no track saved. */
export const NO_TRACK = "__none__";

export function trackLabel(track: string): string {
  return track === NO_TRACK ? "No track saved" : track;
}

export type AuditStamp = { readonly auditId: string; readonly action: string; readonly at: number };

export type ApplicationRow = {
  readonly applicationId: string;
  readonly company: string;
  readonly roleTitle: string;
  readonly source: string;
  readonly stage: string;
  readonly outcome: string | null;
  readonly ctcMinInr: number | null;
  readonly ctcMaxInr: number | null;
  readonly appliedAt: number;
  readonly updatedAt: number;
};

export type RoundRow = {
  readonly roundId: string;
  readonly company: string | null;
  readonly kind: string;
  readonly sequence: number;
  readonly scheduledFor: number | null;
  readonly happenedAt: number | null;
  readonly verdict: string | null;
  readonly interviewer: string | null;
};

export type DebriefRow = {
  readonly debriefId: string;
  readonly company: string | null;
  readonly roleTitle: string | null;
  readonly kind: string | null;
  readonly writtenAt: number;
  readonly characters: number;
  readonly extractedAt: number | null;
  readonly modelPin: string | null;
};

export type MockRow = {
  readonly sessionId: string;
  readonly company: string | null;
  readonly roundKind: string;
  readonly difficulty: string;
  readonly startedAt: number;
  readonly endedAt: number | null;
  readonly turns: number;
  readonly modelPin: string | null;
};

export type CompetencyRow = {
  readonly skillId: string;
  readonly label: string;
  readonly family: string | null;
  readonly practice: string | null;
  readonly observations: number;
  readonly gaps: number;
  readonly strengths: number;
  readonly lastObservedAt: number | null;
};

export type Dossier = {
  readonly ok: true;
  readonly audit: AuditStamp;
  readonly identity: {
    readonly userId: string;
    readonly email: string;
    readonly displayName: string | null;
    readonly createdAt: number;
    readonly lastSeenAt: number;
  };
  readonly profile: {
    readonly targetTrack: string | null;
    readonly yearsExperience: number | null;
    readonly ctcTargetMinInr: number | null;
    readonly ctcTargetMaxInr: number | null;
    readonly mockDifficulty: string;
    readonly updatedAt: number;
  } | null;
  readonly domains: readonly string[];
  readonly pass: Pass | null;
  readonly passes: readonly Pass[];
  readonly counts: Counts;
  readonly applications: readonly ApplicationRow[];
  readonly rounds: readonly RoundRow[];
  readonly debriefs: readonly DebriefRow[];
  readonly mocks: readonly MockRow[];
  readonly competencies: readonly CompetencyRow[];
};

export type DebriefBody = {
  readonly ok: true;
  readonly debriefId: string;
  readonly body: string;
  readonly writtenAt: number;
  readonly roundKind: string | null;
  readonly company: string | null;
  readonly audit: AuditStamp;
};

export const REFUSED = "refused";
export const DELETED = "account_deleted";

async function read<T>(path: string): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, { credentials: "same-origin" });
  } catch {
    throw new Error("unreachable");
  }
  const parsed = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
  if (response.status === 403) throw new Error(REFUSED);
  if (!response.ok || parsed === null || parsed.ok === false) {
    throw new Error(parsed?.error ?? `http_${response.status}`);
  }
  return parsed as T;
}

export type RosterQuery = {
  readonly segment: Segment;
  readonly query: string;
  readonly track: string;
  readonly page: number;
  readonly size: number;
};

export const DEFAULT_PAGE_SIZE = 25;

export const EMPTY_ROSTER_QUERY: RosterQuery = {
  segment: "all",
  query: "",
  track: "",
  page: 1,
  size: DEFAULT_PAGE_SIZE,
};

function rosterParams(options: RosterQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (options.segment !== "all") params.set("segment", options.segment);
  if (options.query.trim().length > 0) params.set("q", options.query.trim());
  if (options.track.length > 0) params.set("track", options.track);
  if (options.page > 1) params.set("page", String(options.page));
  if (options.size !== DEFAULT_PAGE_SIZE) params.set("size", String(options.size));
  return params;
}

export function rosterPath(options: RosterQuery): string {
  const tail = rosterParams(options).toString();
  return tail.length === 0 ? "/api/candidates" : `/api/candidates?${tail}`;
}

/** The filter, without the page: an export is of everything that matches. */
export function exportPath(options: RosterQuery): string {
  const params = rosterParams({ ...options, page: 1, size: DEFAULT_PAGE_SIZE });
  params.set("export", "roster");
  return `/api/candidates?${params.toString()}`;
}

export async function getRoster(options: RosterQuery): Promise<Roster> {
  return await read<Roster>(rosterPath(options));
}

/** Names everybody it returns, and writes one ledger row for each of them. */
export async function exportRoster(options: RosterQuery): Promise<RosterExport> {
  return await read<RosterExport>(exportPath(options));
}

export async function openCandidate(userId: string): Promise<Dossier> {
  return await read<Dossier>(`/api/candidates?user=${encodeURIComponent(userId)}`);
}

export async function readDebrief(userId: string, debriefId: string): Promise<DebriefBody> {
  return await read<DebriefBody>(
    `/api/candidates?user=${encodeURIComponent(userId)}&debrief=${encodeURIComponent(debriefId)}`,
  );
}

const LAKH = 100_000;

export function lakhs(inr: number | null): string | null {
  if (inr === null || inr <= 0) return null;
  const value = inr / LAKH;
  const shown = value >= 100 ? value.toFixed(0) : value.toFixed(1);
  return `${shown.replace(/\.0$/, "")}L`;
}

export function ctcBand(min: number | null, max: number | null): string | null {
  const low = lakhs(min);
  const high = lakhs(max);
  if (low !== null && high !== null) return low === high ? low : `${low} to ${high}`;
  if (low !== null) return `${low} and up`;
  if (high !== null) return `up to ${high}`;
  return null;
}

export function personName(row: {
  readonly displayName: string | null;
  readonly email: string;
}): string {
  if (row.displayName !== null && row.displayName.trim().length > 0) return row.displayName.trim();
  const at = row.email.indexOf("@");
  return at > 0 ? row.email.slice(0, at) : row.email;
}

export function initials(row: {
  readonly displayName: string | null;
  readonly email: string;
}): string {
  const name = personName(row);
  const words = name.split(/[\s._-]+/).filter((one) => one.length > 0);
  const first = words[0] ?? name;
  const second = words[1];
  const letters = second === undefined ? first.slice(0, 2) : `${first[0] ?? ""}${second[0] ?? ""}`;
  return letters.toUpperCase();
}

export type Tone = "live" | "warning" | "spent" | "none";

export function passState(
  pass: Pass | null,
  now: number,
): { readonly text: string; readonly note: string | null; readonly tone: Tone } {
  if (pass === null) return { text: "No pass", note: null, tone: "none" };
  const name = pass.planName ?? "Pass";
  if (pass.status === "cancelled") {
    return { text: `${name}, cancelled`, note: null, tone: "spent" };
  }
  if (pass.expiresAt <= now || pass.status === "expired") {
    return { text: `${name}, expired`, note: null, tone: "spent" };
  }
  const days = Math.max(0, Math.ceil((pass.expiresAt - now) / 86_400_000));
  const note = pass.grantedBy === null ? null : "Granted by an admin";
  const tone: Tone = days <= 14 ? "warning" : "live";
  return { text: `${name}, ${days} ${days === 1 ? "day" : "days"} left`, note, tone };
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** The design's Expiry column, as a date and the days between here and it. */
export function expiryLabel(pass: Pass | null, now: number): string {
  if (pass === null) return "No pass";
  if (pass.status === "cancelled") return `Cancelled, was ${onDay(pass.expiresAt)}`;
  if (pass.expiresAt <= now) return `Ended ${onDay(pass.expiresAt)}`;
  const days = Math.max(0, Math.ceil((pass.expiresAt - now) / 86_400_000));
  return `${days} ${days === 1 ? "day" : "days"}, to ${onDay(pass.expiresAt)}`;
}

export function activityLine(counts: Counts): string {
  const parts = [
    plural(counts.applications, "application", "applications"),
    plural(counts.rounds, "round", "rounds"),
    plural(counts.debriefs, "debrief", "debriefs"),
    plural(counts.mocks, "mock", "mocks"),
  ];
  return parts.join(", ");
}

export function sinceLabel(at: number | null, now: number): string {
  if (at === null) return "never";
  const days = Math.floor((now - at) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${plural(months, "month", "months")} ago`;
  return `${plural(Math.floor(months / 12), "year", "years")} ago`;
}

export function onDay(at: number): string {
  return new Date(at).toISOString().slice(0, 10);
}

export const SEGMENT_LABEL: Record<Segment, string> = {
  all: "Everyone",
  pass: "Holding a pass",
  expiring: "Pass ends within 14 days",
  offers: "Accepted an offer",
  dormant: "Quiet for 30 days",
};

const STAGE_LABEL: Record<string, string> = {
  prospect: "Prospect",
  applied: "Applied",
  screening: "Screening",
  in_rounds: "In rounds",
  offer: "Offer",
  closed: "Closed",
};

const OUTCOME_LABEL: Record<string, string> = {
  offer_accepted: "Offer accepted",
  offer_declined: "Offer declined",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
  went_quiet: "Went quiet",
};

const SOURCE_LABEL: Record<string, string> = {
  referral: "Referral",
  board: "Job board",
  recruiter: "Recruiter",
  direct: "Direct",
  other: "Other",
};

const ROUND_LABEL: Record<string, string> = {
  recruiter_screen: "Recruiter screen",
  dsa: "DSA",
  system_design: "System design",
  machine_coding: "Machine coding",
  domain: "Domain",
  behavioural: "Behavioural",
  hiring_manager: "Hiring manager",
  bar_raiser: "Bar raiser",
  hr: "HR",
  something_else: "Something else",
};

const VERDICT_LABEL: Record<string, string> = {
  advanced: "Advanced",
  rejected: "Rejected",
  awaiting: "Awaiting a reply",
  cancelled: "Cancelled",
};

function titled(raw: string): string {
  return raw
    .split(/[_\s]+/)
    .filter((one) => one.length > 0)
    .map((one) => `${(one[0] ?? "").toUpperCase()}${one.slice(1)}`)
    .join(" ");
}

export function stageLabel(stage: string): string {
  return STAGE_LABEL[stage] ?? titled(stage);
}

export function outcomeLabel(outcome: string | null): string | null {
  if (outcome === null) return null;
  return OUTCOME_LABEL[outcome] ?? titled(outcome);
}

export function sourceLabel(source: string): string {
  return SOURCE_LABEL[source] ?? titled(source);
}

export function roundLabel(kind: string | null): string {
  if (kind === null) return "Round";
  return ROUND_LABEL[kind] ?? titled(kind);
}

export function verdictLabel(verdict: string | null): string {
  if (verdict === null) return "Not sat yet";
  return VERDICT_LABEL[verdict] ?? titled(verdict);
}

export function competencyStanding(row: CompetencyRow): string {
  if (row.gaps > 0 && row.strengths > 0) {
    return `${row.strengths} landed, ${row.gaps} did not`;
  }
  if (row.gaps > 0) return `Named as a gap in ${plural(row.gaps, "round", "rounds")}`;
  if (row.strengths > 0) return `Landed in ${plural(row.strengths, "round", "rounds")}`;
  return "Not named yet";
}

export function pageSummary(view: {
  readonly page: number;
  readonly pages: number;
  readonly total: number;
  readonly shown: number;
}): string {
  if (view.total === 0) return "No candidates match";
  return `Showing ${view.shown} of ${view.total}, page ${view.page} of ${view.pages}`;
}

export function debriefLine(row: DebriefRow): string {
  const where = row.company === null ? roundLabel(row.kind) : `${roundLabel(row.kind)} at ${row.company}`;
  const read = row.extractedAt === null ? "not read by the model yet" : "read by the model";
  return `${where}, ${plural(row.characters, "character", "characters")}, ${read}`;
}

export function refusalSentence(reason: string): string {
  const words: Record<string, string> = {
    refused: "Your session is not on the admin allowlist. Sign in again with an admin address.",
    account_deleted:
      "This person has asked for deletion. Their rows are being destroyed and nothing here will serve them.",
    not_found: "That candidate is not there any more. Reload the roster.",
    unreachable: "The console could not reach the server. Nothing has been changed.",
    subscription_not_found: "That pass is not there any more. Reload the record.",
    pass_cancelled: "That pass has already been cancelled, so there is nothing to change.",
    days_invalid: "A pass lasts a whole number of days, one or more.",
    user_not_found: "No account holds that address.",
    plan_not_found: "That plan is not there, or it has been retired.",
    plan_required: "Choose a plan first. A pass is always a pass to something.",
    unknown_export: "That export does not exist.",
  };
  return words[reason] ?? "The roster did not load. Nothing has been changed.";
}
